import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, Images, ChevronLeft, ChevronRight, Plus, Expand, GripVertical, Trash2, Save } from "lucide-react";
import { ContentBlock } from "@/types/case-study";
import { useImageUpload } from "@/hooks/domains/storage/useImageUpload";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";

interface CarouselBlockProps {
  block: ContentBlock;
  onUpdate: (content: Record<string, unknown>) => void;
  onRemove?: () => void;
  isActive?: boolean;
  isRemovable?: boolean;
  preview?: boolean;
}

export const CarouselBlock: React.FC<CarouselBlockProps> = ({
  block,
  onUpdate,
  onRemove,
  isActive = false,
  isRemovable = true,
  preview = false,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  // Local state for caption editing
  const [localCaption, setLocalCaption] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const {
    uploading,
    handleImageUpload,
  } = useImageUpload({
    user,
    onImageUrlChange: (urls) => {
      const currentImages = (block.content?.images as Array<{id: string; url: string; order: number}>) || [];
      const newImages = urls.map((url, index) => ({
        id: crypto.randomUUID(),
        url,
        order: currentImages.length + index,
      }));
      onUpdate({
        ...block.content,
        images: [...currentImages, ...newImages],
      });
    },
    bucketName: "case-study-images",
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleImageUpload(e);
  };


  const handleRemoveImage = (imageId: string) => {
    const images = ((block.content?.images as Array<{id: string; url: string; order: number}>) || []).filter(img => img.id !== imageId);
    onUpdate({
      ...block.content,
      images,
    });
    // Adjust current slide if needed
    if (currentSlide >= images.length && images.length > 0) {
      setCurrentSlide(images.length - 1);
    }
  };

  const handleUpdateCaption = (imageId: string, caption: string) => {
    const images = ((block.content?.images as Array<{id: string; url: string; order: number; caption?: string}>) || []).map(img =>
      img.id === imageId ? { ...img, caption } : img
    );
    onUpdate({
      ...block.content,
      images,
    });
  };

  const nextSlide = () => {
    const images = (block.content?.images as Array<{id: string; url: string; order: number}>) || [];
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    const images = (block.content?.images as Array<{id: string; url: string; order: number}>) || [];
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const images = (block.content?.images as Array<{id: string; url: string; order: number; caption?: string}>) || [];
  const currentImage = images[currentSlide];

  // Sync local caption with current image's caption
  useEffect(() => {
    const imageCaption = currentImage?.caption || "";
    setLocalCaption(imageCaption);
    setHasUnsavedChanges(false);
  }, [currentImage?.id, currentImage?.caption]);

  const handleCaptionChange = (caption: string) => {
    setLocalCaption(caption);
    const hasChanges = caption !== (currentImage?.caption || "");
    setHasUnsavedChanges(hasChanges);
  };

  const handleSaveCaption = () => {
    if (currentImage) {
      handleUpdateCaption(currentImage.id, localCaption);
      setHasUnsavedChanges(false);
    }
  };

  // Preview mode - only show images
  if (preview) {
    if (images.length === 0) {
      return null; // Don't render anything if no images
    }

    return (
      <div className="space-y-2">
        <div className="relative">
          <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative">
            {currentImage && (
              <img
                src={currentImage.url}
                alt={currentImage.caption || `Carousel image ${currentSlide + 1}`}
                className="w-full h-full object-cover"
              />
            )}

            {/* Navigation arrows - only show if multiple images */}
            {images.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Slide counter - only show if multiple images */}
          {images.length > 1 && (
            <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
              {currentSlide + 1} / {images.length}
            </div>
          )}
        </div>

        {/* Caption display in preview mode */}
        {currentImage?.caption && (
          <p className="text-sm text-gray-600 text-center italic px-4">
            {currentImage.caption}
          </p>
        )}

        {/* Thumbnail navigation - only show if multiple images */}
        {images.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2 justify-center">
            {images.map((image, index) => (
              <button
                key={image.id}
                onClick={() => goToSlide(index)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                  index === currentSlide
                    ? 'border-orange-500'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <img
                  src={image.url}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`group relative ${isActive ? '' : ''}`}>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Content Area */}
        <div className="p-6">
        {images.length > 0 ? (
          <div className="space-y-4">
            {/* Main carousel display */}
            <div className="relative">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative">
                {currentImage && (
                  <img
                    src={currentImage.url}
                    alt={currentImage.caption || `Carousel image ${currentSlide + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
                
                {/* Navigation arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevSlide}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={nextSlide}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              
              {/* Slide counter */}
              {images.length > 1 && (
                <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                  {currentSlide + 1} / {images.length}
                </div>
              )}

              {/* Action buttons - Delete and Expand */}
              {images.length > 0 && (
                <div className="absolute top-2 left-2 flex gap-2">
                  {isActive && (
                    <button
                      onClick={() => handleRemoveImage(currentImage.id)}
                      className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      title="Remove current image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
                    <DialogTrigger asChild>
                      <button
                        className="w-8 h-8 bg-white/90 text-gray-700 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-sm"
                        title="Expand image"
                      >
                        <Expand className="w-4 h-4" />
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
                      <div className="relative w-full h-[95vh] flex items-center justify-center">
                        {/* Close button */}
                        <button
                          onClick={() => setIsExpanded(false)}
                          className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/10 text-white rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                          title="Close"
                        >
                          <X className="w-5 h-5" />
                        </button>

                        {/* Main expanded image */}
                        <div className="relative w-full h-full flex items-center justify-center p-12">
                          {currentImage && (
                            <img
                              src={currentImage.url}
                              alt={currentImage.caption || `Carousel image ${currentSlide + 1}`}
                              className="max-w-full max-h-full object-contain"
                            />
                          )}

                          {/* Navigation arrows for expanded view */}
                          {images.length > 1 && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  prevSlide();
                                }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 text-white rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                              >
                                <ChevronLeft className="w-6 h-6" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  nextSlide();
                                }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 text-white rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                              >
                                <ChevronRight className="w-6 h-6" />
                              </button>
                            </>
                          )}

                          {/* Slide counter in expanded view */}
                          {images.length > 1 && (
                            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1.5 rounded-full">
                              {currentSlide + 1} / {images.length}
                            </div>
                          )}

                          {/* Caption if available */}
                          {currentImage?.caption && (
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-4 py-2 rounded-lg max-w-2xl text-center">
                              {currentImage.caption}
                            </div>
                          )}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </div>

            {/* Caption input for current image - above thumbnails */}
            {currentImage && (
              <div className="flex gap-2 mt-4">
                <Input
                  value={localCaption}
                  onChange={(e) => handleCaptionChange(e.target.value)}
                  placeholder="Add a caption..."
                  className="text-sm flex-1"
                />
                {hasUnsavedChanges && (
                  <Button
                    onClick={handleSaveCaption}
                    size="sm"
                    className="px-3"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                )}
              </div>
            )}

            {/* Thumbnail navigation */}
            <div className="flex gap-2 overflow-x-auto pb-2 justify-center mt-4">
              {images.map((image, index) => (
                <div key={image.id} className="flex-shrink-0">
                  <button
                    onClick={() => goToSlide(index)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${
                      index === currentSlide
                        ? 'border-orange-500'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                  {/* Caption indicator */}
                  {image.caption && (
                    <div className="w-16 mt-1 flex justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500" title="Has caption" />
                    </div>
                  )}
                </div>
              ))}

              {/* Add new image button - always visible */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex-shrink-0 w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors"
                title={uploading ? "Uploading..." : "Add images"}
              >
                <Plus className="w-4 h-4 text-gray-400" />
              </button>
            </div>

          </div>
        ) : (
          <div className="text-center py-8">
            <Images className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Create an image carousel</p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
              size="sm"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Uploading..." : "Choose Images"}
            </Button>
          </div>
        )}
        </div>

        {/* Remove block button */}
        {isRemovable && onRemove && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-lg"
            title="Delete this content block"
          >
            <X className="w-3 h-3" />
          </button>
        )}

        {/* Hidden file input - always present for image uploads */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    </div>
  );
};
